export const API_BASE_URL = process.env.REACT_APP_API_BASE || 'https://api.smarteaming.com';

// Includes response-body consumption, not just receipt of headers.
const DEFAULT_TIMEOUT_MS = 20_000;

/** Most BE routes wrap a success response as `{ success: true, ...data }`, but
 *  not all — several intentionally omit `success` (e.g. `/me/teams-data`,
 *  `/contracts/*`, `/schedule/get-work-history`, `/team-settings/get-team-data`).
 *  Check the target route's own wrapper/comment before assuming this applies.
 *  Existing wrappers keep their own precise inline literal types; use this for
 *  new ones only when the route genuinely returns `success: true`. */
export type ApiSuccess<T = {}> = { success: true } & T;

/** BE's fixed error envelope (see smarteaming-api's `validate` middleware /
 *  route handlers): `{ error }` always, `details` only on 400 validation
 *  failures. Extra fields (e.g. `feature`) appear on some specific errors. */
export interface ApiErrorBody {
  error?: string;
  details?: Array<{ field?: string; message?: string }>;
  [key: string]: unknown;
}

const AUTH_ENDPOINTS = new Set([
  'account/signin',
  'account/me',
  'account/signout',
  'account/store-token',
  'account/verify-password',
  'account/lookup-invite',
  'account/reset-password',
  'account/reset-password-confirm',
  'account/join-team',
  'create-team',
]);

function isAuthEndpoint(endpoint: string): boolean {
  return AUTH_ENDPOINTS.has(endpoint.replace(/^\/+/, '').split('?')[0]);
}

function isCredentialFailure(data: ApiErrorBody | undefined): boolean {
  return data?.error === 'invalid_password' || data?.error === 'Invalid password';
}

/** Thrown by apiCall on any non-2xx response. A real class (not a bagged-on
 *  `any`) so new call sites can `catch (err) { if (err instanceof ApiError) }`
 *  instead of `catch (err: any)` — existing `catch (err: any)` call sites are
 *  unaffected, same shape as before. */
export class ApiError extends Error {
  status: number;
  data: ApiErrorBody | undefined;
  /** Set when apiCall already reacted to this error (e.g. dispatched
   *  `api:feature-disabled`) — callers can skip showing their own toast. */
  handled?: boolean;

  constructor(status: number, data: ApiErrorBody | undefined) {
    super(`API error: ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/** Build the normal API error and notify global UI for errors whose message is
 * owned centrally. Callers can use `handled` to avoid a second toast. */
export function createApiError(
  status: number,
  data: ApiErrorBody | undefined,
  endpoint: string,
): ApiError {
  const err = new ApiError(status, data);
  if (status === 401 && !isAuthEndpoint(endpoint) && !isCredentialFailure(data)) {
    err.handled = true;
    try {
      window.dispatchEvent(new CustomEvent('api:unauthorized', { detail: { endpoint } }));
    } catch {}
  } else if (status === 403 && data?.error === 'feature_disabled') {
    err.handled = true;
    try {
      window.dispatchEvent(new CustomEvent('api:feature-disabled', {
        detail: { feature: data?.feature, endpoint },
      }));
    } catch {}
  }
  return err;
}

export async function apiCall<T = any>(
  endpoint: string,
  options?: { method?: 'GET' | 'POST' | 'DELETE' | 'PATCH'; body?: any; headers?: Record<string, string>; signal?: AbortSignal; timeoutMs?: number }
): Promise<T> {
  const { method = 'POST', body, headers, signal, timeoutMs = DEFAULT_TIMEOUT_MS } = options ?? {};
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const defaultHeaders: Record<string, string> = body && !isFormData ? { 'Content-Type': 'application/json' } : {};
  const timeoutController = new AbortController();
  const abort = () => timeoutController.abort();
  if (signal?.aborted) abort();
  else signal?.addEventListener('abort', abort, { once: true });
  const timeoutId = setTimeout(abort, timeoutMs);
  try {
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method,
      credentials: 'include',
      headers: { ...defaultHeaders, ...headers },
      body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
      signal: timeoutController.signal,
    });
    const contentType = response.headers?.get?.('content-type') || '';
    // Some native/test fetch adapters expose json() without a Headers object.
    // Prefer the real content type, but retain compatibility with those adapters.
    const isJson = contentType
      ? contentType.includes('application/json')
      : typeof response.json === 'function';

    if (!response.ok) {
      let data: ApiErrorBody | undefined;
      try { data = isJson ? await response.json() : { error: await response.text() }; } catch {}
      throw createApiError(response.status, data, endpoint);
    }
    if (isJson) return await response.json();
    // Wrap plain-text responses so callers always get an object
    const text = await response.text();
    return { message: text } as T;
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener('abort', abort);
  }
}
